import { getNewPasswordErrorKey } from '../lib/authValidation';
import { apiClient } from '../lib/apiClient';

/**
 * Self-service account deletion (backend src/routes/meAccount.routes.ts):
 *   DELETE /api/v1/me  { password }  → 200 { data: { deleted: true } }
 *
 * Errors (ApiError.code): 403 REAUTH_FAILED (wrong password), 429 RATE_LIMITED,
 * 502 REAUTH_UNAVAILABLE / COGNITO_ERROR (nothing deleted, retry later).
 */
export const REAUTH_FAILED = 'REAUTH_FAILED';
export const RATE_LIMITED = 'RATE_LIMITED';

export async function deleteOwnAccount(password: string): Promise<void> {
  await apiClient('/api/v1/me', { method: 'DELETE', body: { password } });
}

/**
 * Settings → Account (backend src/routes/meAccount.routes.ts):
 *   GET   /api/v1/me/profile                                → { data: OwnProfile }
 *   PATCH /api/v1/me/profile  { firstName, lastName }       → { data: OwnProfile }
 *   POST  /api/v1/me/password { currentPassword, newPassword } → { data: { changed: true } }
 *
 * Errors (ApiError.code): 400 VALIDATION_ERROR / INVALID_PASSWORD (policy),
 * 403 WRONG_PASSWORD (current password wrong), 429 RATE_LIMITED,
 * 502 PROFILE_UPDATE_FAILED / PASSWORD_CHANGE_UNAVAILABLE (nothing changed).
 */
export const WRONG_PASSWORD = 'WRONG_PASSWORD';
export const INVALID_PASSWORD = 'INVALID_PASSWORD';

export interface OwnProfile {
  email: string;
  firstName: string;
  lastName: string;
}

export async function fetchOwnProfile(): Promise<OwnProfile> {
  const res = await apiClient.get<{ data: OwnProfile }>('/api/v1/me/profile');
  return res.data;
}

export async function updateOwnProfile(input: { firstName: string; lastName: string }): Promise<OwnProfile> {
  const res = await apiClient.patch<{ data: OwnProfile }>('/api/v1/me/profile', input);
  return res.data;
}

export async function changeOwnPassword(currentPassword: string, newPassword: string): Promise<void> {
  await apiClient.post('/api/v1/me/password', { currentPassword, newPassword });
}

/** The Cognito password policy (backend validators/authFields.ts newPasswordField; 8–256 characters). */
export function meetsPasswordPolicy(password: string): boolean {
  return getNewPasswordErrorKey(password) === null;
}

/**
 * Self-service data export (GDPR Art. 15/20, backend GET /api/v1/me/export):
 * the complete JSON export of the signed-in user's data. 429 RATE_LIMITED
 * after 3 exports per hour.
 */
export async function fetchOwnDataExport(): Promise<unknown> {
  return apiClient.get<unknown>('/api/v1/me/export');
}

/** `reefguide-export-YYYY-MM-DD.json` — the name the backend sends (UTC date). */
export function dataExportFilename(now: Date = new Date()): string {
  return `reefguide-export-${now.toISOString().slice(0, 10)}.json`;
}

/** Offers `data` to the browser as a JSON file download. */
export function saveJsonFile(data: unknown, filename: string): void {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.rel = 'noopener';
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Revoke after the click has been handled (some browsers need the URL a moment longer).
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
